import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { IconWrapper } from '@flexo/general';
import { Button } from '@flexo/atoms';
import { useDetailsListData, DetailsListType, DetailsListConfig } from '@flexo/hooks';
import styles from './details-list.module.scss';

export interface DetailsListProps {
  type: DetailsListType;
}

export function DetailsList({ type }: DetailsListProps) {
  const { t } = useTranslation();
  const modules = useSelector((state: any) => state.modules);
  
  const {
    isGroup,
    expanded,
    setExpanded,
    singleConfig,
    groupConfigs,
    totalItems,
    hasExpandCollapse,
  } = useDetailsListData(type);

  // UI rendering function for individual details list items
  function renderDetailsList(config: DetailsListConfig) {
    const { title, isClickable = false, subtitle, dataList, onTitleClick } = config;

    const {
      Details__Wrapper__Compact,
      Details__Paper,
      Details__Title,
      Details__Title__Clickable,
      Details__Subtitle,
      Details__Content,
      Details__Content__Name,
    } = styles;

    const handleTitleClick = () => {
      if (isClickable && onTitleClick) {
        onTitleClick();
      }
    };

    return (
      <div
        key={uuidv4()}
        className={Details__Wrapper__Compact}
      >
        <div className={Details__Paper}>
          {isClickable ? (
            <p
              onClick={handleTitleClick}
              className={`heading4 ${Details__Title} ${Details__Title__Clickable}`}
            >
              {title}
              <IconWrapper iconName="chevron-right" />
            </p>
          ) : (
            <p className={Details__Title}>{title}</p>
          )}

          {subtitle && (
            <p className={Details__Subtitle}>{subtitle}</p>
          )}

          <div>
            {dataList.map((item, index) => (
              <div key={`${item.title}_${index}_${uuidv4()}`} className="flex flex-column">
                <span className={Details__Content}>
                  {item.value || '-'}
                </span>
                <span 
                  className={Details__Content__Name}
                  style={{ marginBottom: index === dataList.length - 1 ? 0 : undefined }}
                >
                  {item.title}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Render single item
  function renderSingle() {
    if (!singleConfig) return null;
    return renderDetailsList(singleConfig);
  }

  // Render group of items
  function renderGroup() {
    const {
      Sites__List__Wrapper,
      Sites__List__Wrapper__Flat,
      Sites__List__Wrapper__Expanded,
      Sites__List__Expand__Button__Wrapper,
      Sites__List__Expand__Button,
      Tall,
    } = styles;

    return (
      <div
        key={`${type}_list_${uuidv4()}`}
        className={`${
          ['sm', 'xs', 'xxs'].includes(modules.breakpoint) 
            ? Sites__List__Wrapper__Flat 
            : Sites__List__Wrapper
        } ${modules.breakpoint === 'sm' && Tall} ${expanded && Sites__List__Wrapper__Expanded}`}
      >
        {groupConfigs.map((config, index) => {
          // For memberSites, only show first 4 items unless expanded
          if (type === 'memberSites' && !expanded && index >= 4) {
            return null;
          }

          const isInactive = config.isInactive;

          return (
            <div
              key={`item_${index}_${uuidv4()}`}
              style={{ opacity: isInactive ? 0.5 : 1 }}
              className={!['sm', 'xs', 'xxs'].includes(modules.breakpoint) && index < groupConfigs.length - 1 ? 'mb-1' : ''}
            >
              {renderDetailsList(config)}
            </div>
          );
        })}

        {hasExpandCollapse && (
          <div className={Sites__List__Expand__Button__Wrapper}>
            <Button
              variant="secondary"
              size="small"
              label={expanded
                ? t('widgets.memberSitesDetails.collapse')
                : t('widgets.memberSitesDetails.expand', {
                    sitesNumber: totalItems,
                  })}
              onClick={() => setExpanded(!expanded)}
            />
          </div>
        )}
      </div>
    );
  }

  return isGroup ? renderGroup() : renderSingle();
}

export default DetailsList; 